/**
 * useEvents Hook - Phase 2C-2
 * 
 * Fetches events for a pool.
 * Uses EventService pattern.
 */

import { useState, useEffect, useCallback } from 'react';
import { Event } from '../types';
import { EventService, ApiError } from '../api';

interface UseEventsResult {
  events: Event[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  getEventById: (eventId: string) => Event | undefined;
  availableEvents: Event[];
  lockedEvents: Event[];
}

export function useEvents(poolId: string): UseEventsResult {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    if (!poolId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await EventService.getAll(poolId);
      setEvents(data);
    } catch (err) {
      // 404 means feature not available yet
      if (err instanceof ApiError && err.status === 404) {
        setEvents([]);
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to load events');
      }
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const getEventById = useCallback(
    (eventId: string) => events.find(e => e.event_id === eventId),
    [events]
  );

  const now = new Date();
  const availableEvents = events.filter(e => {
    // Accept 'open' or 'upcoming' status
    if (e.status !== 'open' && e.status !== 'upcoming') return false;
    if (e.lock_time && new Date(e.lock_time) <= now) return false;
    return true;
  });

  const lockedEvents = events.filter(e => {
    if (e.status === 'locked' || e.status === 'completed' || e.status === 'cancelled') return true;
    if (e.lock_time && new Date(e.lock_time) <= now) return true;
    return false;
  });

  return {
    events,
    loading,
    error,
    refetch: fetchEvents,
    getEventById,
    availableEvents,
    lockedEvents,
  };
}
